import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { initializePushNotifications, isFirebaseConfigured } from '../utils/firebaseConfig';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Initialize Firebase push notifications for the current user
  const initializePushForUser = useCallback(async () => {
    console.log('[Push] Init starting — checking Firebase config...');
    if (!isFirebaseConfigured()) {
      console.warn('[Push] BLOCKED: Firebase not configured (check REACT_APP_FIREBASE_* env vars)');
      return;
    }
    console.log('[Push] Firebase configured. Permission state:', typeof Notification !== 'undefined' ? Notification.permission : 'no-Notification-API');
    
    try {
      const result = await initializePushNotifications();
      if (result?.success) {
        console.log('[Push] ✅ Token registered with backend. Token preview:', (result.token || '').substring(0, 20) + '...');
      } else {
        console.warn('[Push] ❌ Setup failed:', result?.reason || 'unknown');
      }
    } catch (error) {
      console.error('[Push] ❌ Exception:', error);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      
      // Initialize push notifications after user is loaded
      initializePushForUser();
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [initializePushForUser, logout]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  // Automatic Stay End (Feb 2026) — keep the client-side `user` object in
  // sync with the server so a guest whose stay ended (noon of check-out
  // day) transitions to Community mode without needing to log out or
  // refresh. Two triggers:
  //   1. Tab becomes visible again (`visibilitychange`) — instant catch-up
  //      when the guest returns to the app.
  //   2. A 60-second interval while the tab is visible — matches the
  //      backend scheduler cadence so any transition is picked up within
  //      one polling cycle.
  // Silent — no toast on refresh; the UI just re-renders when `user` flips.
  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    const silentRefresh = async () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const response = await axios.get(`${API}/auth/me`);
        if (!cancelled) setUser(response.data);
      } catch (_) {
        // Ignore transient errors; the main fetchUser handles auth failures.
      }
    };
    const intervalId = setInterval(silentRefresh, 60_000);
    const onVisibility = () => {
      if (typeof document !== 'undefined' && !document.hidden) silentRefresh();
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }
    return () => {
      cancelled = true;
      clearInterval(intervalId);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
  }, [token]);

  const login = useCallback(async (email, password) => {
    try {
      // Strip any stale Authorization header before the login request so that
      // an expired token from a previous session doesn't make the backend
      // reject this brand-new login with 401.
      delete axios.defaults.headers.common['Authorization'];
      try { localStorage.removeItem('token'); } catch (_) {}
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      // Reset "remind later" so install banner reappears on next login (until installed)
      try { localStorage.removeItem('ic_install_remind_later'); } catch (_) {}
      
      // Initialize push notifications after successful login
      initializePushForUser();
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  }, [initializePushForUser]);

  const register = useCallback(async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      const { access_token, user: newUser } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(newUser);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Initialize push notifications after successful registration
      initializePushForUser();
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  }, [initializePushForUser]);

  const refreshUser = useCallback(async () => {
    if (token) {
      await fetchUser();
    }
  }, [token, fetchUser]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    user,
    loading,
    login,
    register,
    logout,
    refreshUser
  }), [user, loading, login, register, logout, refreshUser]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
